import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePullToRefresh, unirGestos } from '../../hooks/usePullToRefresh';
import { useMantenerApretado } from '../../hooks/useMantenerApretado';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { PAGINA, BotonFlotante } from '../ui/Pantalla';
import { toast } from 'react-hot-toast';
import { LuTriangleAlert, LuX, LuMapPin, LuPlus, LuRoute, LuDownload, LuArchive, LuList, LuSquareCheck, LuTrash2, LuArchiveRestore, LuCalendarRange, LuEllipsis } from 'react-icons/lu';
import api from '../../services/api';
import BusquedaBar from '../ui/BusquedaBar';
import { M } from '../servicio/ServicioUI';
import { colorTecnico, ETAPAS } from '../../utils/estados';
import { coincideTodo } from '../../utils/busqueda';
import { getTodayISO, formatDateISO } from '../../utils/dateUtils';
import IniciarTrabajoSheet from '../presupuesto/IniciarTrabajoSheet';
import ModalDespacharPresupuesto from '../presupuesto/ModalDespacharPresupuesto';
import EjecutarAdminSheet from '../servicio/EjecutarAdminSheet';
import CobroSheet from '../servicio/CobroSheet';
import DetalleTrabajoSheet from './DetalleTrabajoSheet';
import CorregirCobroSheet from './CorregirCobroSheet';
import OrdenForm from '../ordenes/OrdenForm';
import VisitaForm from '../ordenes/VisitaForm';
import ReprogramarSheet from '../ordenes/ReprogramarSheet';
import FichaVisitaSheet from '../dashboard/FichaVisitaSheet';
import { horaOrden, resumenVentanas, parseVentanas } from '../../utils/ordenes';
import ModalShell from '../ui/ModalShell';
import CierreMensualModal from '../cliente/CierreMensualModal';
import ConfirmDialog from '../ui/ConfirmDialog';
import { exportarServiciosCSV } from '../../utils/exportarCSV';
import { buildGoogleMapsRouteUrl } from '../../utils/clienteUtils';
import TrabajoFila, { ENCABEZADO_GRID, GRID_GAP } from './TrabajoFila';
import TrabajoEditorModal from './TrabajoEditorModal';
import { trabajoHecho } from '../../utils/pdf/pdfShared';
import { pdfDeTrabajo } from '../../utils/pdf/trabajo';
import { NuevoSheet, TrabajoMenu, ElegirEmpresaSheet } from './TrabajoMenus';
import NuevoPedidoSheet from '../empresa/NuevoPedidoSheet';
import AvatarTecnico from '../ui/AvatarTecnico';
import { totalServicio } from '../../utils/descuento';
import { nombreMes } from '../../utils/pdf/cierreMensual';
import HistorialEnBusqueda from './HistorialEnBusqueda';
import PedidoDetalle from '../empresa/PedidoDetalle';
import AgendarPedidoSheet from '../empresa/AgendarPedidoSheet';
import ConfirmarRutaSheet from '../empresa/ConfirmarRutaSheet';
import ArmarDiaSheet from './ArmarDiaSheet';
import AgendaTrabajos from './AgendaTrabajos';
import { GRUPOS, grupoDe, FILTROS, bloquesDe, visitaAtrasada } from './gruposTrabajos';
import { PRIORIDAD } from '../../utils/prioridad';

// ─────────────────────────────────────────────────────────────────────────────
// Trabajos (2-oct-2026) — una sola pantalla para todo el recorrido de un trabajo:
// Presupuesto → Asignado → En camino → En el lugar → Hecho → Facturado → Cobrado.
// Antes el mismo trabajo vivía en 3 lugares (Presupuestos, Despacho y las pestañas
// de cobro de Servicio Técnico), cada uno con sus nombres de estado. Acá no cambia
// ningún dato: se juntan /servicios y /ordenes y cada trabajo cae en UNA etapa.
// Cada fila tiene un solo botón: el próximo paso que le toca al admin.
// ─────────────────────────────────────────────────────────────────────────────

// Opción A: los 7 pasos se agrupan en 3 preguntas (+ cobrados aparte)
const PEDIDO_PARA_AGENDAR = ['NUEVO', 'NO_ATENDIDO', 'PAUSADO'];
// Reservado por la empresa para un día que ya se cerró (25-oct-2026): una urgencia que no entró sola
const pideCerrado = (iso) => {
    const hoy = getTodayISO();
    if (iso <= hoy) return true;
    const d = new Date(); d.setDate(d.getDate() + 1); if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    const siguiente = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const ahora = new Date();
    return iso === siguiente && (ahora.getHours() * 60 + ahora.getMinutes()) >= 17 * 60 + 30;
};
const ETAPA = { ...Object.fromEntries(ETAPAS.map(e => [e.id, e])), ARCHIVADO: { id: 'ARCHIVADO', label: 'Archivado', familia: 'hacer', nivel: 1, color: 'var(--etapa-hacer)', tx: 'var(--etapa-hacer-tx)', bg: 'var(--etapa-hacer-bg)', sobre: '#fff' } };
const ETAPA_DE_ORDEN = { PENDIENTE: 'ASIGNADO', EN_CAMINO: 'CAMINO', EN_SITIO: 'LUGAR', NO_ATENDIDO: 'ASIGNADO' };
const ABIERTAS = ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO'];
// Una línea bajo el título del bloque cuando el nombre solo no alcanza (16-oct-2026)
const AYUDA_BLOQUE = { 'A coordinar': 'El técnico elige el día dentro de lo que puede el cliente', 'Sin agendar': 'Todavía sin técnico o sin día' };
const plural = (n, una, varias) => `${n} ${n === 1 ? una : varias}`;
const atrasadaTxt = (f) => `Atrasada ${plural(diasDesde(f), 'día', 'días')}`;
// Visita abierta de un día anterior (23-oct-2026, testeo integral F3): no solo las que nunca arrancaron;
// también las que quedaron "en camino" o "en el lugar" y nadie cerró.
const ABIERTA = ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO'];
const atrasadaNota = (o) => (o.estado === 'NO_ATENDIDO' ? 'No lo atendieron: reprogramala'
    : o.estado === 'PENDIENTE' ? atrasadaTxt(o.fechaProgramada)
    : `Quedó ${o.estado === 'EN_SITIO' ? 'en el lugar' : 'en camino'} hace ${plural(diasDesde(o.fechaProgramada), 'día', 'días')}`);
// Estado de la visita en una palabra (la segunda línea de la columna Fecha)
const estadoVisita = (o, et) => (et === 'CAMINO' ? 'Salió' : et === 'LUGAR' ? 'Trabajando'
    : o.sinConfirmar ? (o.noPuedeEn ? (propone(o) ? `✋ No puede · propone ${propone(o)}` : '✋ Tentativa · no puede') : o.confirmadaEn ? '⏳ Tentativa · puede' : '⏳ Tentativa · sin respuesta')
    : o.confirmadaEn ? '✓ Confirmó' : 'Sin "Ok, voy"');

// Lo que propuso el técnico al decir que no puede ("Puede Mié 4 de 10 a 12 · motivo", 1-nov-2026)
const propone = (o) => { const m = /^Puede (.+?)(?: · |$)/.exec(String(o.noPuedeMotivo || '')); return m ? m[1] : null; };
// Fecha tentativa (1-nov-2026): confirmarla es un toque desde la lista ("Cliente confirmó"), sin abrir la visita.
// Si el técnico avisó que no puede, el paso es reprogramarla.
const accionTentativa = (o) => (o.estado === 'PENDIENTE' && o.sinConfirmar ? (o.noPuedeEn ? 'reprogramar' : 'confirmarCliente') : null);

const diasDesde = (f) => {
    if (!f) return null;
    const d = new Date(String(f).slice(0, 10) + 'T00:00:00');
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};
const fechaCorta = (f) => {
    if (!f) return '';
    const s = String(f).slice(0, 10);
    if (s === getTodayISO()) return 'Hoy';
    const [, m, d] = s.split('-');
    return d && m ? `${d}/${m}` : s;
};
const primerNombre = (n) => (n || '').trim().split(' ')[0] || '';
const totalItems = (s) => (s.items || []).reduce((a, i) => a + Number(i.costo || 0), 0);
// Total con descuento: el del backend (s.totales). Antes la lista mostraba el total sin descuento.
const totalTrabajo = (s) => totalServicio(s);
// Va al cierre del mes: todo trabajo (no venta) de un cliente con tarifa mensual. Antes se miraba
// si las notas decían "cierre mensual", y un trabajo cargado desde el formulario común quedaba
// suelto en "Por cobrar" sin monto (14-oct-2026).
// 25-oct-2026: un trabajo sin equipos no entra en el cierre (se cuenta por equipo): va suelto, con "Cobrar".
const esCierreMensual = (s) => (s.items || []).length > 0 && ((s.clienteTarifaMensual && s.servicioTipo !== 'VENTA')
    || (totalItems(s) === 0 && /cierre mensual/i.test(s.observaciones || '')));
const PENDIENTE_CIERRE = ['COMPLETADO', 'PENDIENTE_FACTURACION', 'FACTURADO'];
// Tramo del cierre de un trabajo (25-oct-2026). Antes se agrupaba por mes calendario y MODO AGUA cierra
// del 23 al 22: lo ya cobrado del período aparecía partido en "Septiembre" y "Este mes" con otras fechas.
// Ahora: lo facturado esperando el pago; lo hecho desde el último cierre ("actual"); y lo hecho antes del
// último cierre que quedó afuera ("antes"), que se revisa aparte. corte = día siguiente al último facturado.
const tramoCierre = (s, corte) => (s.estado === 'FACTURADO' ? 'FACT'
    : corte && String(s.fecha || '').slice(0, 10) < corte ? 'ANTES' : 'ACTUAL');

// Desde cuándo se traen los cobrados (el resto de las etapas siempre viene completo)
const PERIODOS = [
    { id: 'mes',  label: 'Este mes' },
    { id: 'prev', label: 'Desde el mes pasado' },
    { id: '3m',   label: 'Últimos 3 meses' },
    { id: 'anio', label: 'Este año' },
];
const desdePeriodo = (p) => {
    const h = new Date();
    const d = p === 'prev' ? new Date(h.getFullYear(), h.getMonth() - 1, 1)
        : p === '3m' ? new Date(h.getFullYear(), h.getMonth() - 2, 1)
        : p === 'anio' ? new Date(h.getFullYear(), 0, 1)
        : new Date(h.getFullYear(), h.getMonth(), 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export default function TrabajosManager({ nuevoInicial = null, clienteInicial = null, onInicialConsumido, onIrInicio = null }) {
    const [servicios, setServicios] = useState([]);
    const [ordenes, setOrdenes]     = useState([]);
    // Pedidos de empresas sin agendar (20-oct-2026): antes en la bandeja "Pedidos", ahora la etapa "Por agendar"
    const [pedidos, setPedidos]     = useState([]);
    const [pedidoAbierto, setPedidoAbierto] = useState(null);
    const [agendarPedido, setAgendarPedido] = useState(null);
    const [rutaEmpresa, setRutaEmpresa] = useState(null);   // ruta que propuso una empresa (23-oct-2026)
    const [armarDia, setArmarDia] = useState(false);        // armar el día solo (25-oct-2026)
    const [mas, setMas] = useState(false);                  // menú "Más" de la barra (26-oct-2026)
    const [tecnicos, setTecnicos]   = useState([]);
    const [cargando, setCargando]   = useState(true);

    // Se puede llegar desde Clientes ("Cobrar") con un grupo y una búsqueda ya puestos
    const [abrir] = useState(() => { try { const v = JSON.parse(sessionStorage.getItem('trabajosAbrir') || 'null'); sessionStorage.removeItem('trabajosAbrir'); return v; } catch { return null; } });
    // 27-oct-2026: Por agendar · Agendados · Por cobrar · (Cobrados). Un número del Inicio llega como filtro
    const [filtro, setFiltro] = useState(abrir?.filtro && FILTROS[abrir.filtro] ? abrir.filtro : null);
    const [grupo, setGrupo]   = useState(abrir?.grupo === 'hacer' ? 'agendar' : abrir?.grupo || (abrir?.filtro === 'atrasadas' ? 'agendados' : 'agendar'));
    const [etapa, setEtapa]   = useState(null);    // sub-filtro dentro del grupo
    const [tec, setTec]       = useState('');      // '' todos · '__SIN__' · nombre
    const [busqueda, setBusqueda] = useState(abrir?.busqueda || '');

    // Modales (se reusan los mismos de las pantallas viejas)
    const [iniciar, setIniciar]       = useState(null);
    const [despachar, setDespachar]   = useState(null);
    const [ejecutar, setEjecutar]     = useState(null);
    const [cobrar, setCobrar]         = useState(null);
    const [detalle, setDetalle]       = useState(null);
    const [corregir, setCorregir]     = useState(null);   // { servicio, total }
    const [ordenEditar, setOrdenEditar] = useState(null);
    const [reprogramar, setReprogramar] = useState(null);
    const [atrasada, setAtrasada]     = useState(null);
    const [ficha, setFicha]           = useState(null); // visita sin presupuesto: su ficha
    const [cierreCliente, setCierreCliente] = useState(null);
    // Dónde arranca el período abierto de cada cliente mensual (día siguiente al último facturado), clienteId → AAAA-MM-DD
    const [cortes, setCortes] = useState({});
    // Total estimado de cada renglón del cierre (lo calcula el backend), "clienteId|tramo" → $
    const [estimados, setEstimados] = useState({});
    const pendientesCierre = useMemo(() => servicios.filter(s => s.clienteId && esCierreMensual(s) && PENDIENTE_CIERRE.includes(s.estado)), [servicios]);
    useEffect(() => {
        const ids = [...new Set(pendientesCierre.map(s => s.clienteId))];
        if (!ids.length) return;
        let vivo = true;
        const mes = getTodayISO().slice(0, 7);
        Promise.all(ids.map(id => api.get(`/clientes/${id}/cierre-mensual`, { params: { mes } })
            .then(r => [id, r.data?.sugeridoDesde ? String(r.data.sugeridoDesde).slice(0, 10) : null]).catch(() => [id, null])))
            .then(pares => { if (vivo) setCortes(Object.fromEntries(pares)); });
        return () => { vivo = false; };
    }, [pendientesCierre]);
    // Renglones del cierre: período de cada uno (lo que abre el cierre) y sus trabajos
    const tramos = useMemo(() => {
        const t = {};
        pendientesCierre.forEach(s => {
            const corte = cortes[s.clienteId] || null;
            const tramo = tramoCierre(s, corte);
            const k = `${s.clienteId}|${tramo}`;
            const f = String(s.fecha || '').slice(0, 10) || getTodayISO();
            const g = t[k] || (t[k] = { clienteId: s.clienteId, tramo, corte, desde: f, hasta: f, ids: [], monto: 0 });
            if (f < g.desde) g.desde = f;
            if (f > g.hasta) g.hasta = f;
            g.ids.push(s.id);
            g.monto += Number(s.montoFinal) || 0;
        });
        Object.values(t).forEach(g => {
            // El período abierto va desde el último cierre hasta hoy; lo demás, de su primer a su último trabajo
            g.periodo = g.tramo === 'ACTUAL' ? { desde: g.corte || g.desde, hasta: g.hasta > getTodayISO() ? g.hasta : getTodayISO() }
                : { desde: g.desde, hasta: g.hasta };
        });
        return t;
    }, [pendientesCierre, cortes]);
    useEffect(() => {
        const pedir = Object.entries(tramos).filter(([, g]) => g.tramo !== 'FACT');
        if (!pedir.length) return;
        let vivo = true;
        Promise.all(pedir.map(([k, g]) => api.get(`/clientes/${g.clienteId}/cierre-mensual`, { params: g.periodo })
            .then(r => {
                const d = r.data || {};
                const mios = new Set(g.ids);
                const filas = (d.filas || []).filter(f => mios.has(f.servicioId));
                // Si en el período hay trabajos ya cobrados, se estima solo lo pendiente
                if (filas.length === (d.filas || []).length) return [k, Number(d.total) || null];
                return [k, d.precioUnitario ? Math.round(Number(d.precioUnitario) * filas.length * 1.21) : null];
            }).catch(() => [k, null])))
            .then(pares => { if (vivo) setEstimados(Object.fromEntries(pares)); });
        return () => { vivo = false; };
    }, [tramos]);

    // Lo que antes vivía en Servicio Técnico y Presupuestos
    const [nuevoAbierto, setNuevoAbierto] = useState(false);
    const [editor, setEditor]         = useState(null);   // { modo, servicio, clienteId }
    const [nuevaVisita, setNuevaVisita] = useState(false);
    // Pedido de empresa (1-nov-2026): las empresas con cobro mensual tienen su propio camino en "+ Nuevo"
    const [empresasMensuales, setEmpresasMensuales] = useState([]);
    const [elegirEmpresa, setElegirEmpresa] = useState(null);     // lista, si hay más de una
    const [pedidoDe, setPedidoDe] = useState(null);              // { id, nombre }
    useEffect(() => {
        api.get('/clientes', { params: { size: 1000 } })
            .then(r => setEmpresasMensuales((r.data?.content || r.data || []).filter(c => c.tieneTarifa).map(c => ({ id: c.id, nombre: c.nombre }))))
            .catch(() => {});
    }, []);
    const [menuFila, setMenuFila]     = useState(null);
    const [confirmar, setConfirmar]   = useState(null);   // { tipo: 'archivar'|'eliminar', servicio }
    // 'lista' | 'agenda' (27-oct-2026). Desde el Inicio puede llegar con la Agenda en un día (zona sugerida)
    const [vista, setVista]           = useState(abrir?.vista === 'agenda' ? 'agenda' : 'lista');
    const [seleccionando, setSeleccionando] = useState(false);
    const [seleccion, setSeleccion]   = useState(() => new Set());
    const [verArchivados, setVerArchivados] = useState(false);
    const [archivados, setArchivados] = useState([]);
    const [periodo, setPeriodo]       = useState('mes');

    // Abrir un trabajo puntual (21-oct-2026): desde la ficha de una visita del Panel, "Ver en Trabajos"
    // lleva a ESE trabajo, en la pestaña donde está (antes caía en "Por hacer" aunque estuviera cobrado)
    const [abrirId, setAbrirId] = useState(() => (abrir?.servicioId && Date.now() - (abrir.en || 0) < 15000 ? abrir.servicioId : null));
    useEffect(() => {
        const h = (e) => {
            if (!e.detail?.servicioId) return;
            try { sessionStorage.removeItem('trabajosAbrir'); } catch { /* nada */ }
            setAbrirId(e.detail.servicioId);
        };
        window.addEventListener('trabajos:abrir', h);
        return () => window.removeEventListener('trabajos:abrir', h);
    }, []);

    // Entrar con algo para crear (desde el Panel o desde Clientes)
    useEffect(() => {
        if (!nuevoInicial) return;
        // Desde la tarjeta "Ruta de hoy" del Panel (27-oct-2026): abre Armar día
        if (nuevoInicial === 'armar') setArmarDia(true);
        else setEditor({ modo: nuevoInicial, servicio: null, clienteId: clienteInicial?.id || null });
        onInicialConsumido && onInicialConsumido();
    }, [nuevoInicial, clienteInicial, onInicialConsumido]);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const inicioMes = desdePeriodo(periodo);
            const enUnAno = new Date(); enUnAno.setFullYear(enUnAno.getFullYear() + 1);
            const [abiertos, cobrados, ords] = await Promise.all([
                api.get('/servicios', { params: { estado: 'PRESUPUESTO,APROBADO,EN_PROGRESO,COMPLETADO,PENDIENTE_FACTURACION,FACTURADO', page: 0, size: 2000, sort: 'fechaServicio,desc' } }),
                api.get('/servicios', { params: { estado: 'COBRADO,REALIZADO', desde: inicioMes, page: 0, size: 2000, sort: 'fechaServicio,desc' } }),
                api.get('/ordenes', { params: { desde: '2020-01-01', hasta: formatDateISO(enUnAno) } }),
            ]);
            const lista = (r) => r.data?.content || (Array.isArray(r.data) ? r.data : []);
            // Bajo del testeo: antes la lista se cortaba en 500/300 sin avisar
            const cortada = [abiertos, cobrados].some(r => r.data?.totalElements > lista(r).length);
            if (cortada) toast('Hay más trabajos de los que se muestran: usá el buscador o achicá el período', { icon: 'ℹ️', id: 'trabajos-cortados' });
            setServicios([...lista(abiertos), ...lista(cobrados)]);
            setOrdenes(Array.isArray(ords.data) ? ords.data : []);
            api.get('/pedidos-empresa').then(r => setPedidos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
            if (verArchivados) {
                const ar = await api.get('/servicios', { params: { estado: 'ARCHIVADO', page: 0, size: 1000, sort: 'fechaServicio,desc' } });
                setArchivados(lista(ar));
            }
        } catch {
            toast.error('No se pudieron cargar los trabajos');
        } finally {
            setCargando(false);
        }
    }, [periodo, verArchivados]);

    useEffect(() => { cargar(); }, [cargar]);
    // Pedido abierto desde un aviso (push, campanita o el Mapa)
    const abrirPedidoId = useCallback(async (id) => {
        try { const r = await api.get(`/pedidos-empresa/${id}`); setPedidoAbierto(r.data); } catch { toast.error('No se encontró el pedido'); }
    }, []);
    useEffect(() => {
        let pend = null;
        try { pend = sessionStorage.getItem('abrirPedido'); sessionStorage.removeItem('abrirPedido'); } catch { /* */ }
        if (pend) abrirPedidoId(pend);
        const onAbrir = (e) => { try { sessionStorage.removeItem('abrirPedido'); } catch { /* */ } abrirPedidoId(e.detail); };
        window.addEventListener('abrir-pedido', onAbrir);
        // Los pedidos llegan en cualquier momento: se miran cada 30 s
        const t = setInterval(() => api.get('/pedidos-empresa').then(r => setPedidos(Array.isArray(r.data) ? r.data : [])).catch(() => {}), 30000);
        return () => { window.removeEventListener('abrir-pedido', onAbrir); clearInterval(t); };
    }, [abrirPedidoId]);
    useEffect(() => {
        api.get('/ordenes/tecnicos').then(r => setTecnicos(r.data || [])).catch(() => {});
    }, []);

    // ── Armado de filas: cada trabajo en UNA etapa ──────────────────────────
    const armado = useMemo(() => {
        const hoy = getTodayISO();
        const sueltos = [];   // cada trabajo de un cierre mensual, para cuando se busca (28-oct-2026)
        const ordenAbiertaDe = {};
        ordenes.filter(o => ABIERTAS.includes(o.estado) && o.presupuestoId)
            .forEach(o => { ordenAbiertaDe[o.presupuestoId] = o; });
        const idsServicios = new Set(servicios.map(s => s.id));
        const out = [];
        const grupos = {}; // cierre mensual: un renglón por cliente y mes (y etapa)
        const mesHoy = hoy.slice(0, 7);
        const agrupar = (s, etapa, equipos, base) => {
            const tramo = tramoCierre(s, cortes[s.clienteId] || null);
            const k = `${s.clienteId}|${tramo}`;
            const t = tramos[k] || { desde: mesHoy + '-01', hasta: hoy, periodo: null, corte: null };
            const rango = t.desde === t.hasta ? fechaCorta(t.desde) : `${fechaCorta(t.desde)} al ${fechaCorta(t.hasta)}`;
            const viejo = (diasDesde(t.desde) || 0) > 35;
            const g = grupos[k] || (grupos[k] = {
                key: `cm${k}`, etapa, cliente: s.clienteNombre, clienteId: s.clienteId, periodo: t.periodo, tramo,
                equipos: 0, tecnicos: new Set(), busca: '', monto: null, accion: 'cierre',
                fecha: tramo === 'ACTUAL' && t.corte ? `Desde ${fechaCorta(t.corte)}` : rango,
                nota: tramo === 'FACT' ? 'Facturado: esperando el pago'
                    : tramo === 'ANTES' ? 'Quedó afuera del último cierre'
                        : viejo ? 'Período largo: falta facturar' : 'Se factura al cerrar el período',
                alerta: tramo === 'ANTES' || (tramo === 'ACTUAL' && viejo),
                botonLabel: tramo === 'FACT' ? 'Cobrar' : tramo === 'ANTES' ? 'Revisar' : 'Ver período',
                servicios: [], fechaOrden: t.desde,
            });
            if (!g.cliente) g.cliente = s.clienteNombre;
            g.servicios.push(s); // para poder abrir cada trabajo del mes
            g.equipos += equipos.length || 1;
            if (s.usuarioNombre) g.tecnicos.add(s.usuarioNombre);
            g.busca += ' ' + base.busca;
            // Buscando "Alicia" se ve su trabajo, no solo el renglón del mes de Modo Agua
            sueltos.push({ ...base, key: `cm-s${s.id}`, etapa, cliente: [s.sedeLugar, s.clienteNombre].filter(Boolean).join(' · ') || base.cliente, tecnico: s.usuarioNombre || '', fecha: fechaCorta(s.fechaCompletado || s.fecha),
                nota: 'Va al cierre del mes', accion: null, fechaOrden: String(s.fechaCompletado || s.fecha || '') });
        };

        servicios.forEach(s => {
            const ord = ordenAbiertaDe[s.id];
            const equipos = (s.items || []).filter(i => i.equipoSerial && i.equipoSerial !== 'MOSTRADOR');
            const lugar = [s.sedeNombre, s.sedeDireccion].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ');
            const base = {
                key: `s${s.id}`, servicio: s, orden: ord || null,
                cliente: s.clienteNombre || `#${s.id}`,
                detalle: [equipos.length ? `${equipos.length} equipo${equipos.length !== 1 ? 's' : ''}` : null, lugar,
                    equipos.slice(0, 2).map(i => i.equipoSerial).join(', ') || null].filter(Boolean).join(' · '),
                busca: [s.clienteNombre, s.sedeNombre, s.sedeLugar, s.sedeDireccion, s.nroDocumento, s.usuarioNombre,
                    ...equipos.map(i => i.equipoSerial)].filter(Boolean).join(' ').toLowerCase(),
                monto: Number(s.montoFinal) > 0 ? Number(s.montoFinal) : totalTrabajo(s),
                esVenta: s.servicioTipo === 'VENTA',
            };
            let fila;
            // fechaOrden: la fecha que manda en el orden dentro de su etapa
            const fo = (...fs) => String(fs.find(Boolean) || '').replace(' ', 'T');
            if (s.enEspera) {
                fila = { ...base, etapa: 'PRESUPUESTO', tecnico: '', fecha: fechaCorta(s.fecha), nota: 'En espera', accion: 'asignar', fechaOrden: fo(s.fecha) };
            } else if (['PRESUPUESTO', 'APROBADO', 'EN_PROGRESO'].includes(s.estado)) {
                if (ord) {
                    const et = ETAPA_DE_ORDEN[ord.estado];
                    const atr = ABIERTA.includes(ord.estado) && ord.fechaProgramada && ord.fechaProgramada < hoy;
                    fila = {
                        ...base, etapa: et, tecnico: ord.tecnicoNombre || '',
                        fecha: fechaCorta(ord.fechaProgramada) + (ord.horaEstimada ? ` ${ord.horaEstimada}` : ''),
                        nota: atr ? atrasadaNota(ord) : estadoVisita(ord, et),
                        alerta: atr || !!(ord.sinConfirmar && ord.noPuedeEn), dia: ord.fechaProgramada || null,
                        // En el lugar: el próximo paso es cerrarlo (16-oct-2026)
                        accion: atr ? 'atrasada' : accionTentativa(ord) || (et === 'ASIGNADO' ? 'ver' : et === 'LUGAR' ? 'cerrarTrabajo' : 'seguimiento'),
                        fechaOrden: fo(ord.fechaProgramada) + ' ' + horaOrden(ord.horaEstimada),
                    };
                } else if (s.estado === 'EN_PROGRESO') {
                    fila = { ...base, etapa: 'ASIGNADO', tecnico: s.usuarioNombre || '', fecha: fechaCorta(s.fecha), nota: 'Sin visita agendada', alerta: true, accion: 'ejecutar', fechaOrden: fo(s.fecha) };
                } else {
                    const d = diasDesde(s.fecha);
                    fila = { ...base, etapa: 'PRESUPUESTO', tecnico: '', fecha: fechaCorta(s.fecha), nota: d > 14 ? `Hace ${plural(d, 'día', 'días')}` : '', alerta: d > 30, accion: 'asignar', fechaOrden: fo(s.fecha) };
                }
            } else if (s.estado === 'COMPLETADO' || s.estado === 'PENDIENTE_FACTURACION') {
                if (esCierreMensual(s) && s.clienteId) { agrupar(s, 'HECHO', equipos, base); return; }
                const d = diasDesde(s.fechaCompletado || s.fecha);
                fila = {
                    ...base, etapa: 'HECHO', tecnico: s.usuarioNombre || '', fecha: fechaCorta(s.fechaCompletado || s.fecha),
                    nota: s.estado === 'PENDIENTE_FACTURACION' ? 'Falta emitir la factura' : (d > 7 ? `Hace ${d} días sin cobrar` : 'Falta cobrar'),
                    alerta: d > 7,
                    accion: s.estado === 'PENDIENTE_FACTURACION' ? 'facturado' : 'cobrar',
                    fechaOrden: fo(s.fechaCompletado, s.fecha),
                };
            } else if (s.estado === 'FACTURADO') {
                if (esCierreMensual(s) && s.clienteId) { agrupar(s, 'FACTURADO', equipos, base); return; }
                const d = diasDesde(s.fechaFacturacion || s.fecha);
                fila = { ...base, etapa: 'FACTURADO', tecnico: s.usuarioNombre || '', fecha: fechaCorta(s.fechaFacturacion || s.fecha),
                    nota: d > 7 ? `Hace ${d} días sin cobrar` : 'Esperando el pago', alerta: d > 7, accion: 'cobrado', fechaOrden: fo(s.fechaFacturacion, s.fecha) };
            } else if (s.estado === 'COBRADO' || s.estado === 'REALIZADO') {
                fila = { ...base, etapa: 'COBRADO', tecnico: s.usuarioNombre || '', fecha: fechaCorta(s.fechaCobro || s.fecha),
                    nota: s.modalidadCobro === 'EFECTIVO_SIN_FACTURA' ? 'Efectivo' : s.modalidadCobro === 'CON_FACTURA' ? 'Con factura' : '', accion: 'pdf', fechaOrden: fo(s.fechaCobro, s.fecha) };
            }
            // Las ventas no van a un técnico (4-oct-2026): en vez de "Asignar",
            // el paso que corresponde es confirmar la venta (queda cobrada).
            if (fila && fila.esVenta && fila.accion === 'asignar') fila = { ...fila, accion: 'venta', nota: fila.nota || 'Venta sin confirmar' };
            if (fila) out.push(fila);
        });

        // Visitas abiertas sin presupuesto (o con uno que no vino en la lista). 28-oct-2026: también toda
        // visita atrasada que no quedó como fila (no atendidas, o con el trabajo ya en otra etapa), así
        // el número de "atrasadas" del Inicio y la lista son lo mismo.
        const yaAtrasadas = new Set(out.filter(f => f.accion === 'atrasada' && f.orden).map(f => f.orden.id));
        ordenes.filter(o => (ABIERTAS.includes(o.estado) && (!o.presupuestoId || !idsServicios.has(o.presupuestoId)))
            || (visitaAtrasada(o, hoy) && !yaAtrasadas.has(o.id)))
            .forEach(o => {
                const et = ETAPA_DE_ORDEN[o.estado];
                // Pedido de empresa "a coordinar" (16-oct-2026): el día que tiene es provisorio —
                // no se muestra como fecha; va en su propio bloque con lo que pueden
                const coordinar = o.estado === 'PENDIENTE' && parseVentanas(o.ventanasCliente).length > 0;
                const atr = !coordinar && visitaAtrasada(o, hoy);
                // Pedido de empresa ("Motivo · Lugar"): el lugar es lo que distingue una visita de
                // otra del mismo cliente, así que va como título y la empresa abajo
                const [motivoPed, lugarPed] = o.titulo && o.clienteNombre && o.titulo.includes(' · ') && !o.titulo.startsWith('Visita · ')
                    ? [o.titulo.slice(0, o.titulo.indexOf(' · ')), o.titulo.slice(o.titulo.indexOf(' · ') + 3)] : [null, null];
                out.push({
                    key: `o${o.id}`, orden: o, servicio: null, etapa: et,
                    cliente: o.lugar || lugarPed || o.clienteNombre || o.titulo || `Visita #${o.id}`,
                    detalle: o.lugar || lugarPed
                        // Visita de un particular ("Revisar falla · Eva", 1-nov-2026): el nombre ya es el título
                        ? [(o.lugar || lugarPed) !== o.clienteNombre ? o.clienteNombre : null, o.motivo || motivoPed, o.direccion].filter(Boolean).join(' · ')
                        : [o.equiposSerie ? plural(o.equiposSerie.split(',').filter(Boolean).length, 'equipo', 'equipos') : null,
                        o.titulo && o.titulo !== o.clienteNombre ? o.titulo.replace(/^Visita · /, '') : null, o.direccion].filter(Boolean).join(' · '),
                    coordinar,
                    busca: [o.clienteNombre, o.titulo, o.direccion, o.tecnicoNombre].filter(Boolean).join(' ').toLowerCase(),
                    tecnico: o.tecnicoNombre || '',
                    fecha: coordinar ? '' : fechaCorta(o.fechaProgramada) + (o.horaEstimada ? ` ${o.horaEstimada}` : ''),
                    nota: coordinar ? `Pueden ${resumenVentanas(o.ventanasCliente).join(' / ').replace(/ \(\d+ a \d+ h\)/g, '').replace(/ · /g, ' ')}`
                        : atr ? atrasadaNota(o) : estadoVisita(o, et),
                    alerta: atr || !!(o.sinConfirmar && o.noPuedeEn), dia: coordinar ? null : o.fechaProgramada || null,
                    // Visita de un cliente con tarifa mensual: se cobra en el cierre del mes
                    monto: o.tarifaMensual && !Number(o.montoEstimado) ? null : Number(o.montoEstimado) || 0,
                    accion: atr ? 'atrasada' : accionTentativa(o) || (et === 'ASIGNADO' ? 'ver' : 'seguimiento'),
                    fechaOrden: String(o.fechaProgramada || '') + ' ' + horaOrden(o.horaEstimada),
                });
            });

        // Pedidos de empresas sin agendar: arriba de todo en "Por hacer" (urgentes primero)
        pedidos.filter(p => PEDIDO_PARA_AGENDAR.includes(p.estado)).forEach(p => {
            const d = diasDesde(p.creadoEn);
            const eqs = (p.equipos || []).length || 1;
            const lugar = p.lugar && p.lugar !== p.clienteNombre ? p.lugar : null;
            // Ruta que armó la empresa para un día (23-oct-2026): va en ese día, a confirmar
            const ruta = p.rutaFecha && String(p.rutaFecha).slice(0, 10) >= getTodayISO() ? String(p.rutaFecha).slice(0, 10) : null;
            // Prioridad para armar el día (25-oct-2026): urgente va con alerta, el resto se nombra en la nota
            const urgente = p.prioridad ? p.prioridad === 'URGENTE' : !!p.urgente;
            const prio = p.prioridad && p.prioridad !== 'NORMAL' ? PRIORIDAD[p.prioridad]?.label : null;
            out.push({
                key: `p${p.id}`, pedido: p, servicio: null, orden: null, etapa: 'AGENDAR',
                cliente: [p.codigo, lugar || p.clienteNombre || `Pedido #${p.id}`].filter(Boolean).join(' · '),
                detalle: [lugar ? p.clienteNombre : null, p.motivo, plural(eqs, 'equipo', 'equipos'), p.direccion].filter(Boolean).join(' · '),
                busca: [p.codigo, p.clienteNombre, p.lugar, p.direccion, p.motivo, p.detalle, `#${p.id}`, ...(p.equipos || []).map(e => e.serie)].filter(Boolean).join(' ').toLowerCase(),
                tecnico: '', fecha: fechaCorta(ruta || p.creadoEn),
                nota: [ruta ? (pideCerrado(ruta) ? 'Reservado por la empresa, el día ya se cerró: agendalo' : 'Reservado por la empresa')
                    : urgente ? `Urgente${p.prioridadNota ? ` · ${p.prioridadNota}` : ''}` : (p.ventanas || []).length ? `Pueden ${resumenVentanas(p.ventanas).join(' / ').replace(/ \(\d+ a \d+ h\)/g, '').replace(/ · /g, ' ')}`
                    : d > 2 ? `Pedido hace ${plural(d, 'día', 'días')}` : 'Pedido nuevo',
                    !urgente && prio ? `Prioridad ${prio.toLowerCase()}` : null].filter(Boolean).join(' · '),
                alerta: urgente || (!!ruta && pideCerrado(ruta)), monto: null, sinMonto: true, accion: 'agendarPedido',
                dia: ruta, fechaOrden: ruta ? `${ruta} ${String(p.rutaOrden ?? 0).padStart(3, '0')}` : String(p.creadoEn || ''),
            });
        });

        Object.values(grupos).forEach(g => {
            const t = [...g.tecnicos];
            out.push({ ...g, tecnico: t.length === 1 ? t[0] : '', tecnicoTexto: t.length > 1 ? t.map(primerNombre).join(', ') : null,
                detalle: `${g.equipos} equipo${g.equipos !== 1 ? 's' : ''} · tarifa por volumen`,
                monto: g.tramo === 'FACT' ? (tramos[`${g.clienteId}|FACT`]?.monto || null) : (estimados[`${g.clienteId}|${g.tramo}`] ?? null) });
        });

        // Criterio de orden (2-oct-2026), igual en toda la app:
        //  1. alertas arriba de todo;
        //  2. por etapa (en el recorrido del trabajo);
        //  3. dentro de la etapa: lo pendiente, lo más viejo primero (nada queda olvidado
        //     abajo); lo agendado, por fecha y hora; lo cobrado, lo más nuevo primero.
        const ordenEtapa = Object.fromEntries(ETAPAS.map((e, i) => [e.id, i]));
        return { sueltos, filas: out.sort((a, b) =>
            (b.alerta ? 1 : 0) - (a.alerta ? 1 : 0)
            || ordenEtapa[a.etapa] - ordenEtapa[b.etapa]
            || (a.etapa === 'COBRADO'
                ? String(b.fechaOrden || '').localeCompare(String(a.fechaOrden || ''))
                : String(a.fechaOrden || '').localeCompare(String(b.fechaOrden || '')))) };
    }, [servicios, ordenes, estimados, pedidos, tramos, cortes]);
    const { filas, sueltos } = armado;

    // ── Filtros ──────────────────────────────────────────────────────────────
    const q = busqueda.trim().toLowerCase();
    // Multi-palabra y sin acentos ("guemes" encuentra "Güemes"), igual en toda la app
    const pasaBusqueda = (f) => !q || coincideTodo(`${f.busca || ''} ${f.cliente || ''} ${f.detalle || ''}`, q);
    const pasaTec = (f) => !tec || (tec === '__SIN__' ? !f.tecnico : f.tecnico === tec);
    // Mientras se busca, se busca en TODOS los grupos (antes buscaba solo en el grupo
    // abierto y parecía que no encontraba nada)
    const hoyISO = getTodayISO();
    const enEtapa = (f) => (q ? true : filtro ? FILTROS[filtro].es(f, hoyISO) : grupoDe(f, hoyISO) === grupo && (!etapa || f.etapa === etapa));

    useEffect(() => {
        if (!abrirId || cargando) return;
        const id = Number(abrirId);
        setAbrirId(null);
        setBusqueda('');
        const f = filas.find(x => x.servicio?.id === id);
        if (f) {
            const g = grupoDe(f, getTodayISO());
            if (g && g !== 'hoy') { setGrupo(g); setEtapa(null); setFiltro(null); }
            setDetalle(f.servicio);
            return;
        }
        api.get(`/servicios/${id}`).then(r => setDetalle(r.data)).catch(() => toast.error('No se pudo abrir ese trabajo'));
    }, [abrirId, cargando, filas]);

    // Archivados: lista aparte, fuera del recorrido (se ven solo con "Ver archivados")
    const filaSuelta = (s, prefijo = 'a') => {
        const archivado = s.estado === 'ARCHIVADO';
        return {
            key: `${prefijo}${s.id}`, servicio: s, orden: null, etapa: archivado ? 'ARCHIVADO' : 'COBRADO',
            cliente: s.clienteNombre || `#${s.id}`,
            detalle: [s.sedeNombre, s.sedeDireccion].filter(Boolean).join(' · '),
            busca: [s.clienteNombre, s.sedeNombre, s.sedeDireccion, s.usuarioNombre, `#${s.id}`].filter(Boolean).join(' ').toLowerCase(),
            tecnico: s.usuarioNombre || '', fecha: fechaCorta(s.fecha),
            monto: Number(s.montoFinal) > 0 ? Number(s.montoFinal) : totalTrabajo(s), esVenta: s.servicioTipo === 'VENTA',
            nota: archivado ? 'Archivado' : s.modalidadCobro === 'EFECTIVO_SIN_FACTURA' ? 'Efectivo' : s.modalidadCobro === 'CON_FACTURA' ? 'Con factura' : '',
            accion: archivado ? null : 'pdf',
        };
    };
    const filasArchivadas = useMemo(() => archivados.map(s => filaSuelta(s)), [archivados]); // eslint-disable-line react-hooks/exhaustive-deps

    // 3-nov-2026: buscando, también los cobrados de antes del período y los archivados (antes solo se buscaba en
    // lo que estaba cargado y un trabajo viejo o archivado "no existía")
    const [extraBusqueda, setExtraBusqueda] = useState([]);
    useEffect(() => {
        setExtraBusqueda([]);
        const t = busqueda.trim();
        if (t.length < 3 || verArchivados) return undefined;
        let vivo = true;
        const id = setTimeout(() => {
            api.get('/servicios', { params: { busqueda: t, estado: 'COBRADO,REALIZADO,ARCHIVADO', page: 0, size: 100, sort: 'fechaServicio,desc' } })
                .then(r => { if (vivo) setExtraBusqueda(r.data?.content || (Array.isArray(r.data) ? r.data : [])); })
                .catch(() => { /* quedan los resultados de siempre */ });
        }, 400);
        return () => { vivo = false; clearTimeout(id); };
    }, [busqueda, verArchivados]);
    const filasExtra = useMemo(() => {
        const ya = new Set(servicios.map(s => s.id));
        return extraBusqueda.filter(s => !ya.has(s.id)).map(s => filaSuelta(s, 'b'));
    }, [extraBusqueda, servicios]); // eslint-disable-line react-hooks/exhaustive-deps

    const base = verArchivados
        ? filasArchivadas.filter(pasaBusqueda)
        : (q ? [...filas, ...sueltos, ...filasExtra] : filas).filter(f => pasaBusqueda(f) && enEtapa(f));
    const visibles = base.filter(pasaTec);
    const conteo = Object.fromEntries(ETAPAS.map(e => [e.id, filas.filter(f => f.etapa === e.id && grupoDe(f, hoyISO) === grupo && pasaBusqueda(f) && pasaTec(f)).length]));
    // Lo de hoy (en el Inicio): una línea arriba de la lista
    const deHoy = useMemo(() => {
        const vs = ordenes.filter(o => String(o.fechaProgramada || '').slice(0, 10) === hoyISO && ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO', 'COMPLETADA', 'NO_ATENDIDO'].includes(o.estado));
        return { n: vs.length, hechas: vs.filter(o => o.estado === 'COMPLETADA').length };
    }, [ordenes, hoyISO]);
    const atrasadas = filas.filter(f => f.accion === 'atrasada');

    const chips = useMemo(() => {
        const nombres = [...new Set(base.map(f => f.tecnico).filter(Boolean))].sort();
        const sin = base.filter(f => !f.tecnico).length;
        return [
            { id: '', label: 'Todos', count: base.length },
            ...nombres.map(n => ({ id: n, label: primerNombre(n), count: base.filter(f => f.tecnico === n).length, color: colorTecnico(n) })),
            ...(sin ? [{ id: '__SIN__', label: 'Sin técnico', count: sin }] : []),
        ];
    }, [base]);

    const verChips = chips.filter(c => c.id && c.id !== '__SIN__').length >= 2 || !!tec;
    const totalVisible = visibles.reduce((a, f) => a + (f.monto || 0), 0);
    // 27-oct-2026: el total en plata solo donde dice algo (por cobrar y cobrados); en lo demás, cuántos y cuántos son mensuales
    const conPlata = !!q || ['cobrar', 'cobrado'].includes(grupo) || verArchivados;
    const mensuales = visibles.filter(f => f.monto == null && !f.sinMonto).length;
    const tituloTotal = q ? 'Encontrados' : { HECHO: 'Para cobrar', FACTURADO: 'Para cobrar', COBRADO: `Cobrado · ${(PERIODOS.find(p => p.id === periodo)?.label || '').toLowerCase()}`, PRESUPUESTO: 'Presupuestado' }[etapa] || { agendar: 'Por agendar', agendados: 'Agendados', cobrar: 'Para cobrar', cobrado: `Cobrado · ${(PERIODOS.find(p => p.id === periodo)?.label || '').toLowerCase()}` }[grupo];

    const elegirGrupo = (id) => { setGrupo(id); setEtapa(null); setTec(''); setFiltro(null); };
    // Deslizar el dedo a los costados cambia de grupo (como antes en Servicio Técnico)
    const swipeHandlers = useSwipeGesture(verArchivados || q ? [] : GRUPOS.map(g => g.id), grupo, elegirGrupo);

    // ── Acciones ─────────────────────────────────────────────────────────────
    const patchServicio = async (id, estado, msg, extras = {}) => {
        const t = toast.loading('Guardando…');
        try {
            await api.patch(`/servicios/${id}/estado`, { estado, ...extras });
            toast.success(msg, { id: t });
            cargar();
            return true;
        } catch { toast.error('No se pudo actualizar', { id: t }); return false; }
    };
    const patchOrden = async (id, estado, msg) => {
        const t = toast.loading('Guardando…');
        try {
            await api.patch(`/ordenes/${id}/estado`, { estado });
            toast.success(msg, { id: t });
            setAtrasada(null);
            cargar();
        } catch { toast.error('No se pudo actualizar', { id: t }); }
    };
    const guardarOrden = async (id, form) => {
        const t = toast.loading('Guardando…');
        try {
            const { estadoNuevo, ...datos } = form;
            await api.put(`/ordenes/${id}`, datos);
            if (estadoNuevo) await api.patch(`/ordenes/${id}/estado`, { estado: estadoNuevo });
            toast.success('Visita actualizada', { id: t });
            setOrdenEditar(null);
            cargar();
        } catch { toast.error('No se pudo guardar', { id: t }); }
    };
    const pdf = (s, opciones) => pdfDeTrabajo(s, opciones);

    const abrirFila = (f) => {
        if (f.pedido) setPedidoAbierto(f.pedido);
        else if (f.servicio) setDetalle(f.servicio);
        else if (f.orden) setFicha(f.orden);
        // Renglón del mes de un cliente mensual: abre el mes (ahí está la lista de equipos)
        else if (f.accion === 'cierre') setCierreCliente({ id: f.clienteId, nombre: f.cliente, periodo: f.periodo });
        else if (f.servicios?.length === 1) setDetalle(f.servicios[0]);
        else if (f.servicios?.length) setMenuFila(f);
    };
    const BOTON = {
        // 'Agendar' como en los pedidos (23-oct-2026, testeo U6): es el mismo paso, técnico y día
        asignar:     { label: 'Agendar',          primaria: true,  run: f => setIniciar(f.servicio) },
        agendarPedido: { label: 'Agendar',        primaria: true,  run: f => setAgendarPedido(f.pedido) },
        confirmarRuta: { label: 'Ver ruta',       primaria: true,  run: f => {
            const dia = String(f.pedido.rutaFecha).slice(0, 10);
            setRutaEmpresa({ clienteId: f.pedido.clienteId, clienteNombre: f.pedido.clienteNombre, fecha: dia,
                pedidos: pedidos.filter(x => x.clienteId === f.pedido.clienteId && String(x.rutaFecha || '').slice(0, 10) === dia && PEDIDO_PARA_AGENDAR.includes(x.estado))
                    .sort((a, b) => (a.rutaOrden ?? 0) - (b.rutaOrden ?? 0)) });
        } },
        venta:       { label: 'Confirmar venta',  primaria: true,  run: f => patchServicio(f.servicio.id, 'REALIZADO', 'Venta confirmada') },
        ejecutar:    { label: 'Agendar o cerrar', primaria: true,  run: f => setIniciar(f.servicio) },
        reprogramar: { label: 'Reprogramar',      primaria: false, run: f => setReprogramar(f.orden) },
        confirmarCliente: { label: 'Cliente confirmó', primaria: true, run: f => confirmarTentativa(f.orden) },
        ver:         { label: 'Ver',              primaria: false, run: f => abrirFila(f) },
        cerrarTrabajo: { label: 'Cerrar trabajo', primaria: true,  run: f => setEjecutar(f.servicio) },
        // Atrasada: abre "¿qué pasó?" → reprogramar (lo más común), ya se hizo o cancelar
        atrasada:    { label: 'Reprogramar',      primaria: true,  run: f => setAtrasada(f) },
        cobrar:      { label: 'Cobrar',           primaria: true,  run: f => setCobrar(f.servicio) },
        facturado:   { label: 'Ya facturé',      primaria: true,  run: f => patchServicio(f.servicio.id, 'FACTURADO', 'Marcado como facturado') },
        cobrado:     { label: 'Marcar cobrado',   primaria: true,  run: f => patchServicio(f.servicio.id, 'COBRADO', 'Cobrado') },
        cierre:      { label: 'Ver el mes',       primaria: true,  run: f => setCierreCliente({ id: f.clienteId, nombre: f.cliente, periodo: f.periodo }) },
        pdf:         { label: 'Ver PDF',          primaria: false, run: f => pdf(f.servicio) },
    };
    const confirmarTentativa = async (o) => {
        const t = toast.loading('Confirmando…');
        try {
            await api.patch(`/ordenes/${o.id}/fecha-tentativa`, { sinConfirmar: false });
            toast.success(`Confirmada: le avisamos a ${primerNombre(o.tecnicoNombre) || 'el técnico'}`, { id: t });
            cargar();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo confirmar', { id: t }); }
    };
    const textoSeguimiento = (f) => {
        const n = primerNombre(f.tecnico) || 'El técnico';
        return f.etapa === 'CAMINO' ? `${n} va en camino` : `${n} está trabajando`;
    };

    const calcularTotal = (s) => totalTrabajo(s);

    // ⋯ de cada fila
    const duplicar = (s) => setEditor({ modo: 'duplicar', servicio: {
        ...s, id: undefined, estado: 'PRESUPUESTO', nroDocumento: undefined,
        fecha: getTodayISO(), presupuestoOrigenId: undefined, ordenId: undefined,
    } });
    const cambiarEspera = async (s, enEspera) => {
        const t = toast.loading('Guardando…');
        try {
            await api.patch(`/servicios/${s.id}/espera`, { enEspera });
            toast.success(enEspera ? 'Quedó en espera' : 'Retomado: vuelve a Presupuesto', { id: t });
            cargar();
        } catch { toast.error('No se pudo actualizar', { id: t }); }
    };
    const eliminar = async (s) => {
        const t = toast.loading('Eliminando…');
        try {
            await api.delete(`/servicios/${s.id}`);
            toast.success('Eliminado', { id: t });
            cargar();
        } catch { toast.error('No se pudo eliminar', { id: t }); }
    };
    const accionesMenu = {
        detalle: (s) => setDetalle(s),
        // Una visita del cierre mensual: su menú propio (deja que se cierre el anterior)
        menuDe: (s, f) => setTimeout(() => setMenuFila({ servicio: s, cliente: `${f.cliente} · trabajo #${s.id}` }), 250),
        cierre: (f) => setCierreCliente({ id: f.clienteId, nombre: f.cliente }),
        editar: (s, orden = null) => setEditor({ modo: 'editar', servicio: s, orden }),
        duplicar,
        pdf,
        espera: cambiarEspera,
        archivar: (s) => setConfirmar({ tipo: 'archivar', servicio: s }),
        recuperar: (s) => patchServicio(s.id, 'PRESUPUESTO', 'Recuperado como presupuesto'),
        eliminar: (s) => setConfirmar({ tipo: 'eliminar', servicio: s }),
        editarOrden: (o) => setOrdenEditar(o),
        cancelarOrden: (o) => setConfirmar({ tipo: 'cancelarVisita', orden: o }),
        eliminarOrden: (o) => setConfirmar({ tipo: 'eliminarVisita', orden: o }),
    };
    // Visitas sin presupuesto: cancelar (queda en el historial) o eliminar del todo
    const accionVisita = async (tipo, o) => {
        const t = toast.loading(tipo === 'eliminarVisita' ? 'Eliminando…' : 'Cancelando…');
        try {
            if (tipo === 'eliminarVisita') await api.delete(`/ordenes/${o.id}`);
            else await api.patch(`/ordenes/${o.id}/estado`, { estado: 'CANCELADA' });
            toast.success(tipo === 'eliminarVisita' ? 'Visita eliminada' : 'Visita cancelada', { id: t });
            cargar();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo', { id: t }); }
    };

    const elegirNuevo = (q) => {
        if (q === 'pedido') {
            if (empresasMensuales.length === 1) setPedidoDe(empresasMensuales[0]);
            else setElegirEmpresa(empresasMensuales);
        } else if (q === 'visita') setNuevaVisita(true);
        else setEditor({ modo: q, servicio: null });
    };
    // Selección para armar la ruta del día
    const toggleSel = (key) => setSeleccion(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const salirSeleccion = () => { setSeleccionando(false); setSeleccion(new Set()); };
    // Archivados: selección masiva para recuperar o eliminar definitivamente
    const [confirmarMasivo, setConfirmarMasivo] = useState(false);
    const elegidosArchivados = () => visibles.filter(f => seleccion.has(f.key) && f.servicio).map(f => f.servicio);
    const todosElegidos = visibles.length > 0 && visibles.every(f => seleccion.has(f.key));
    const elegirTodos = () => setSeleccion(todosElegidos ? new Set() : new Set(visibles.map(f => f.key)));
    const masivo = async (tipo) => {
        const lista = elegidosArchivados();
        if (!lista.length) return;
        const t = toast.loading(tipo === 'eliminar' ? 'Eliminando…' : 'Recuperando…');
        const res = await Promise.allSettled(lista.map(sv => tipo === 'eliminar'
            ? api.delete(`/servicios/${sv.id}`)
            : api.patch(`/servicios/${sv.id}/estado`, { estado: 'PRESUPUESTO' })));
        const fallas = res.filter(r => r.status === 'rejected').length;
        const ok = lista.length - fallas;
        const verbo = tipo === 'eliminar' ? 'eliminado' : 'recuperado';
        if (fallas) toast.error(`${ok} ${verbo}${ok !== 1 ? 's' : ''} · ${fallas} no se pudo${fallas !== 1 ? 'ieron' : ''}`, { id: t });
        else toast.success(`${ok} ${verbo}${ok !== 1 ? 's' : ''}`, { id: t });
        salirSeleccion();
        cargar();
    };
    const abrirRuta = () => {
        // Pedidos sin agendar (25-oct-2026): se ordenan en "Armar día" con sus ubicaciones, no por el texto
        const elegidos = visibles.filter(f => seleccion.has(f.key));
        if (elegidos.length && elegidos.every(f => f.pedido && PEDIDO_PARA_AGENDAR.includes(f.pedido.estado))) {
            setArmarDia({ solo: elegidos.map(f => f.pedido.id) });
            return;
        }
        const dirs = elegidos
            .map(f => f.servicio?.sedeDireccion || f.orden?.direccion || f.pedido?.direccion).filter(Boolean);
        const url = buildGoogleMapsRouteUrl(dirs);
        if (!url) { toast.error('Ninguno de los elegidos tiene dirección cargada'); return; }
        window.open(url, '_blank');
    };
    const exportar = () => {
        const ss = visibles.map(f => f.servicio).filter(Boolean);
        if (!ss.length) { toast.error('No hay trabajos para exportar'); return; }
        exportarServiciosCSV(ss);
    };

    // Bloques de cada pestaña (27-oct-2026, gruposTrabajos.js): Por agendar → Urgente · Para agendar · A coordinar;
    // Agendados → Atrasadas · Mañana · Próximos días. Lo de hoy está en el Inicio.
    const bloques = useMemo(() => {
        if (vista !== 'lista' || q || verArchivados || filtro) return null;
        return bloquesDe(grupo, visibles, getTodayISO());
    }, [vista, grupo, q, verArchivados, visibles, filtro]);

    const pull = usePullToRefresh(() => cargar());
    // Mantener apretada una fila: entra en selección con esa fila elegida
    const apretado = useMantenerApretado((f) => {
        setSeleccionando(true);
        setSeleccion(prev => { const n = new Set(prev); n.add(f.key); return n; });
    });

    return (
        <div className={PAGINA} {...unirGestos(swipeHandlers, pull.handlers)}>
            {pull.indicador}
            <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6 space-y-3 md:space-y-4">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
                    <div className="hidden md:block">
                        <h2 className="hidden md:block text-2xl md:text-3xl font-black uppercase tracking-tight text-ink">Trabajos</h2>
                        <p className="hidden md:block text-caption text-muted">Cada trabajo, de presupuesto a cobrado, en una sola lista</p>
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <div className="flex-1 md:w-80">
                            <BusquedaBar valor={busqueda} onChange={setBusqueda} placeholder="Cliente, N/S, dirección…" />
                        </div>
                        <button type="button" onClick={() => setNuevoAbierto(true)}
                            className="hidden md:inline-flex h-11 px-4 rounded-xl items-center gap-1.5 bg-[#C9341F] text-white text-label font-black active:scale-95">
                            <LuPlus size={16} /> Nuevo
                        </button>
                    </div>
                </div>

                {/* Atrasadas */}
                {atrasadas.length > 0 && grupo !== 'agendados' && !filtro && !verArchivados && (
                    <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[color:var(--alerta-bg)] text-[color:var(--alerta-tx)] border border-[color:var(--alerta)]/30 text-caption font-bold">
                        <LuTriangleAlert size={16} className="shrink-0" />
                        <span className="flex-1">
                            {atrasadas.length} visita{atrasadas.length !== 1 ? 's' : ''} de días anteriores sigue{atrasadas.length !== 1 ? 'n' : ''} abierta{atrasadas.length !== 1 ? 's' : ''}
                        </span>
                        <button onClick={() => elegirGrupo('agendados')} className="font-black underline">Ver</button>
                    </div>
                )}

                {/* Grupos (opción A, 3-oct-2026): 3 preguntas del día en vez de 7 etapas.
                    Debajo, el detalle de las etapas del grupo (tocables para filtrar). */}
                {!verArchivados && q && (
                    <p className="px-1 text-caption text-muted">Buscando «{busqueda.trim()}» en todos los trabajos (también cobrados viejos y archivados)</p>
                )}
                {q && <HistorialEnBusqueda busqueda={busqueda} onAbrir={(id) => {
                    api.get(`/servicios/${id}`).then(r => setDetalle(r.data)).catch(() => toast.error('No se pudo abrir ese trabajo'));
                }} />}
                {/* Llegó desde un número del Inicio: se ve solo eso, con cómo volver */}
                {filtro && !q && !verArchivados && (
                    <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-panel text-body">
                        <span className="flex-1 font-black text-ink">{FILTROS[filtro].label}</span>
                        <button type="button" onClick={() => setFiltro(null)} className="h-9 px-3 rounded-lg bg-chip text-label font-black text-ink inline-flex items-center gap-1 active:scale-95">
                            <LuX size={14} /> Ver todo
                        </button>
                    </div>
                )}
                {!verArchivados && !q && !filtro && (
                    <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-1 p-1 rounded-2xl bg-chip">
                            {GRUPOS.filter(g => g.id !== 'cobrado').map(g => {
                                const activo = grupo === g.id;
                                const n = filas.filter(f => grupoDe(f, hoyISO) === g.id && pasaBusqueda(f) && pasaTec(f)).length;
                                return (
                                    <button key={g.id} type="button" onClick={() => elegirGrupo(g.id)} aria-pressed={activo}
                                        className={`h-12 md:h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${activo ? 'bg-card shadow-sm' : ''}`}>
                                        <span className={`text-body font-black ${activo ? 'text-ink' : 'text-secondary'}`}>{g.label} <span className="opacity-70">{cargando ? '·' : n}</span></span>
                                        <span className="w-5 h-[3px] rounded-full" style={{ background: g.color }} />
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex items-center justify-between gap-2 px-1 text-caption text-muted">
                            <span className="flex items-center gap-3 min-w-0 overflow-x-auto [scrollbar-width:none]">
                                {(GRUPOS.find(g => g.id === grupo)?.etapas || []).filter(id => conteo[id] > 0 || etapa === id).map(id => (
                                    <button key={id} type="button" onClick={() => setEtapa(e => (e === id ? null : id))}
                                        className={`shrink-0 inline-flex items-center gap-1.5 ${etapa === id ? 'text-ink font-black underline' : ''}`}>
                                        <span className="w-2 h-2 rounded-full" style={{ background: ETAPA[id].color }} />{ETAPA[id].label} {conteo[id]}
                                    </button>
                                ))}
                            </span>
                            <button type="button" onClick={() => elegirGrupo(grupo === 'cobrado' ? 'agendar' : 'cobrado')} className="shrink-0 font-bold underline">
                                {grupo === 'cobrado' ? 'Volver' : 'Cobrados'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Herramientas (26-oct-2026): a la vista lo de todos los días (vista, Armar día, Semana); lo de vez en
                    cuando (elegir para una ruta, exportar, archivados) en "Más". Antes eran siete botones iguales. */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex shrink-0 rounded-xl bg-panel p-1">
                        {[['lista', 'Lista', LuList], ['agenda', 'Agenda', LuCalendarRange]].map(([id, label, Ic]) => (
                            <button key={id} type="button" onClick={() => setVista(id)} aria-pressed={vista === id}
                                className={`h-8 md:h-9 px-2.5 md:px-3 rounded-lg inline-flex items-center gap-1.5 text-label font-bold ${vista === id ? 'bg-card text-ink shadow-sm' : 'text-muted'}`}>
                                <Ic size={14} />{label}
                            </button>
                        ))}
                    </div>
                    {!verArchivados && (
                        <button type="button" onClick={() => setArmarDia(true)}
                            className="h-9 md:h-10 px-2.5 md:px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 text-label font-black bg-brand-red text-white active:scale-95">
                            <span aria-hidden>✨</span>Armar día
                        </button>
                    )}
                    {verArchivados && (
                        <>
                            <button type="button" onClick={() => (seleccionando ? salirSeleccion() : setSeleccionando(true))}
                                className={`h-9 md:h-10 px-2.5 md:px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 text-label font-bold border ${seleccionando ? 'border-brand-red text-ink' : 'border-black/10 dark:border-white/10 text-secondary'}`}>
                                <LuSquareCheck size={14} />{seleccionando ? 'Cancelar' : 'Seleccionar'}
                            </button>
                            <button type="button" onClick={() => { setVerArchivados(false); setEtapa(null); setTec(''); salirSeleccion(); }}
                                className="h-9 md:h-10 px-2.5 md:px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 text-label font-bold border border-brand-red text-ink">
                                <LuArchive size={14} />Volver
                            </button>
                        </>
                    )}
                    {!verArchivados && seleccionando && (
                        <button type="button" onClick={salirSeleccion}
                            className="h-9 md:h-10 px-2.5 md:px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 text-label font-bold border border-brand-red text-ink">
                            <LuX size={14} />Cancelar ruta
                        </button>
                    )}
                    {grupo === 'cobrado' && !verArchivados && (
                        <select value={periodo} onChange={e => setPeriodo(e.target.value)} aria-label="Período de cobrados"
                            className="h-10 px-3 shrink-0 rounded-xl text-label font-bold bg-panel text-ink border border-black/10 dark:border-white/10">
                            {PERIODOS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                    )}
                    {!verArchivados && (
                        <div className="relative ml-auto">
                            <button type="button" onClick={() => setMas(m => !m)} aria-expanded={mas} aria-label="Más opciones"
                                className="h-9 md:h-10 px-2.5 md:px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 text-label font-bold border border-black/10 dark:border-white/10 text-secondary">
                                <LuEllipsis size={16} /><span className="hidden sm:inline">Más</span>
                            </button>
                            {mas && (
                                <>
                                    <button type="button" aria-label="Cerrar" className="fixed inset-0 z-40 cursor-default" onClick={() => setMas(false)} />
                                    <div className="absolute right-0 top-full mt-1.5 z-50 w-64 p-1.5 rounded-2xl bg-card shadow-xl border border-black/[0.06] dark:border-white/[0.08]">
                                        {[
                                            [LuRoute, 'Elegir trabajos para una ruta', () => setSeleccionando(true)],
                                            [LuDownload, 'Exportar', exportar],
                                            [LuArchive, 'Archivados', () => { setVerArchivados(true); setEtapa(null); setTec(''); salirSeleccion(); }],
                                        ].map(([Ic, label, run]) => (
                                            <button key={label} type="button" onClick={() => { setMas(false); run(); }}
                                                className="w-full h-11 px-3 rounded-xl flex items-center gap-2.5 text-left text-body font-bold text-ink hover:bg-panel active:bg-chip">
                                                <Ic size={16} className="text-secondary" />{label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {seleccionando && verArchivados && (
                    <div className="flex flex-wrap items-center gap-2 px-4 py-3 rounded-xl bg-panel text-caption">
                        <span className="flex-1 min-w-[8rem] text-secondary">{seleccion.size} elegido{seleccion.size !== 1 ? 's' : ''}</span>
                        <button type="button" onClick={elegirTodos}
                            className="h-10 px-3 rounded-xl border border-black/10 dark:border-white/10 text-secondary font-bold">{todosElegidos ? 'Ninguno' : 'Todos'}</button>
                        <button type="button" disabled={!seleccion.size} onClick={() => masivo('recuperar')}
                            className="h-10 px-3 rounded-xl inline-flex items-center gap-1.5 border border-black/10 dark:border-white/10 text-ink font-bold disabled:opacity-40">
                            <LuArchiveRestore size={14} /> Recuperar</button>
                        <button type="button" disabled={!seleccion.size} onClick={() => setConfirmarMasivo(true)}
                            className="h-10 px-4 rounded-xl inline-flex items-center gap-1.5 bg-[#C9341F] text-white font-black disabled:opacity-40">
                            <LuTrash2 size={14} /> Eliminar</button>
                    </div>
                )}
                {seleccionando && !verArchivados && (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-panel text-caption">
                        <span className="flex-1 text-secondary">Tocá los trabajos que querés visitar · {seleccion.size} elegido{seleccion.size !== 1 ? 's' : ''}</span>
                        <button type="button" disabled={!seleccion.size} onClick={abrirRuta}
                            className="h-10 px-4 rounded-xl bg-[#C9341F] text-white font-black disabled:opacity-40">Ver ruta</button>
                    </div>
                )}

                {/* Técnico + total */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-3">
                    <div className="flex gap-1.5 md:gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 items-center">
                        {/* Con un solo técnico (o ninguno) el filtro repetía el total: se muestra con dos o más */}
                        {verChips && vista === 'lista' && <span className="hidden md:inline text-label font-bold text-muted shrink-0 pr-1">Técnico</span>}
                        {verChips && vista === 'lista' && chips.map(c => {
                            const activo = tec === c.id;
                            return (
                                <button key={c.id || 'todos'} onClick={() => setTec(c.id)} aria-pressed={activo}
                                    className={`h-8 md:h-10 px-3 md:px-3.5 rounded-full shrink-0 inline-flex items-center gap-2 text-label font-bold border transition-all active:scale-95 ${activo ? 'border-brand-red text-ink bg-[rgba(232,66,47,0.10)]' : 'border-black/10 dark:border-white/10 text-secondary'}`}>
                                    {c.id && c.id !== '__SIN__' && <AvatarTecnico nombre={c.id} size={20} />}
                                    {c.label}<span className="text-muted">{c.count}</span>
                                </button>
                            );
                        })}
                    </div>
                    {vista === 'lista' && (conPlata ? (
                        <div className="flex items-baseline gap-2 text-caption text-muted shrink-0">
                            <span>{verArchivados ? 'Archivados' : tituloTotal}</span>
                            <M valor={totalVisible} className="text-body-lg font-black text-ink" />
                            <span>· {visibles.length} trabajo{visibles.length !== 1 ? 's' : ''}</span>
                        </div>
                    ) : (
                        <p className="text-caption text-muted shrink-0">
                            {visibles.length} trabajo{visibles.length !== 1 ? 's' : ''}{mensuales ? ` · ${mensuales} de tarifa mensual` : ''}
                        </p>
                    ))}
                </div>

                {/* Lo de hoy vive en el Inicio (27-oct-2026): una línea que lleva ahí */}
                {!q && !filtro && !verArchivados && onIrInicio && deHoy.n > 0 && ['agendar', 'agendados'].includes(grupo) && (
                    <button type="button" onClick={onIrInicio}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] text-left active:scale-[0.99]">
                        <span className="text-body font-black text-ink">Hoy: {deHoy.n} visita{deHoy.n !== 1 ? 's' : ''} · {deHoy.hechas} hecha{deHoy.hechas !== 1 ? 's' : ''}</span>
                        <span className="text-caption font-bold text-muted">ver en Inicio ›</span>
                    </button>
                )}

                {/* Agenda: días con su zona y cupo, y las franjas de cada técnico (Agenda + Semana, 27-oct-2026) */}
                {vista === 'agenda' && !verArchivados ? (
                    <AgendaTrabajos ordenes={ordenes} tecnicos={tecnicos} onAbrirVisita={setFicha} fechaInicial={abrir?.fecha || null}
                        onArmar={(fecha) => setArmarDia({ fecha })} onCambio={cargar} />
                ) : cargando ? (
                    <div className="space-y-2">{[1, 2, 3, 4].map(i => <div key={i} className="h-20 rounded-2xl bg-card animate-pulse" />)}</div>
                ) : visibles.length === 0 ? (
                    <div className="py-12 px-6 text-center rounded-2xl border border-dashed border-black/10 dark:border-white/10 space-y-4">
                        <p className="text-body text-muted">
                            {verArchivados ? 'No hay trabajos archivados' : filtro && !q ? `Ya no quedan: ${FILTROS[filtro].label.toLowerCase()} está al día.` : `No hay trabajos ${etapa ? `en "${ETAPA[etapa].label}"` : `en "${GRUPOS.find(g => g.id === grupo)?.label}"`}`}{tec ? ' con este técnico' : ''}{q ? ' para esa búsqueda' : ''}
                        </p>
                        {!verArchivados && filtro && !q && (
                            <button type="button" onClick={() => setFiltro(null)}
                                className="h-11 px-5 rounded-xl inline-flex items-center gap-1.5 bg-chip text-ink text-label font-black active:scale-95">Ver todos los trabajos</button>
                        )}
                        {!verArchivados && !(filtro && !q) && (
                            <button type="button" onClick={() => setNuevoAbierto(true)}
                                className="h-11 px-5 rounded-xl inline-flex items-center gap-1.5 bg-[#C9341F] text-white text-label font-black active:scale-95">
                                <LuPlus size={16} /> Cargar un trabajo
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div className={`fila-pc hidden md:grid ${ENCABEZADO_GRID} ${GRID_GAP} px-4 text-label font-bold uppercase tracking-wider text-muted`}>
                            <span>Etapa</span><span>Cliente</span><span className="hidden 2xl:block">Técnico</span><span>Fecha</span><span className="text-right">Monto</span><span className="text-right">Próximo paso</span><span />
                        </div>
                        {(bloques || [[null, visibles]]).map(([nombre, items]) => (
                            <div key={nombre ?? '__todos'} className="space-y-2">
                                {bloques && (
                                    <p className={`pt-3 px-1 text-label font-black uppercase tracking-widest ${['Atrasadas', 'Urgente'].includes(nombre) ? 'text-[color:var(--alerta-tx)]' : 'text-secondary'}`}>
                                        {nombre} <span className="text-muted">{items.length}</span>
                                        {AYUDA_BLOQUE[nombre] && <span className="block mt-0.5 normal-case tracking-normal font-bold text-caption text-muted">{AYUDA_BLOQUE[nombre]}</span>}
                                    </p>
                                )}
                                {items.map(f => (
                                    <div key={f.key} onTouchStart={e => apretado.onTouchStart(e, f)} onTouchMove={apretado.onTouchMove}
                                        onTouchEnd={apretado.onTouchEnd} onClickCapture={apretado.onClickCapture}>
                                    <TrabajoFila f={f} etapa={ETAPA[f.etapa]} boton={BOTON[f.accion]}
                                        textoSeguimiento={textoSeguimiento}
                                        onAbrir={() => abrirFila(f)}
                                        onMenu={() => (f.pedido ? setPedidoAbierto(f.pedido) : setMenuFila(f))}
                                        seleccionando={seleccionando} seleccionado={seleccion.has(f.key)}
                                        onToggle={() => toggleSel(f.key)} />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* FAB "+" (celular) — mismo lugar en Trabajos, Venta, Clientes y Productos */}
            {!seleccionando && (
                <BotonFlotante onClick={() => setNuevoAbierto(true)} label="Nuevo trabajo" />
            )}

            {/* ── Modales ── */}
            {iniciar && (
                <IniciarTrabajoSheet servicio={iniciar}
                    onYoAhora={() => { setEjecutar(iniciar); setIniciar(null); }}
                    onAsignar={() => { setDespachar(iniciar); setIniciar(null); }}
                    onCerrar={() => setIniciar(null)} />
            )}
            {despachar && (
                <ModalDespacharPresupuesto presupuesto={despachar} calcularTotal={calcularTotal}
                    onCerrar={() => { setDespachar(null); cargar(); }}
                    onDespachado={() => cargar()}
                    onYaHecho={(s) => { setDespachar(null); setEjecutar({ ...despachar, ...s }); }} />
            )}
            {ejecutar && (
                <EjecutarAdminSheet servicio={ejecutar} calcularTotal={calcularTotal}
                    onConfirmar={async (estadoDestino, extras, firmas) => {
                        const s = ejecutar;
                        const ok = await patchServicio(s.id, estadoDestino, 'Trabajo cerrado', extras || {});
                        setEjecutar(null);
                        // PDF del trabajo hecho con las firmas (16-oct-2026)
                        if (ok && firmas?.incluirFirmas && (firmas.firmaCliente || firmas.firmaTecnico)) {
                            api.patch(`/servicios/${s.id}/firmas`, {
                                ...(firmas.firmaCliente ? { firmaCliente: firmas.firmaCliente } : {}),
                                ...(firmas.firmaTecnico ? { firmaTecnico: firmas.firmaTecnico } : {}),
                                ...(firmas.aclaracionCliente ? { aclaracionCliente: firmas.aclaracionCliente } : {}),
                            }).catch(() => toast.error('El trabajo se cerró, pero no se pudieron guardar las firmas'));
                        }
                        if (ok) pdf({ ...s, estado: estadoDestino, observaciones: extras?.observaciones ?? s.observaciones }, { firmas });
                    }}
                    onCerrar={() => setEjecutar(null)} />
            )}
            {cobrar && (
                <CobroSheet servicio={cobrar} calcularTotal={calcularTotal}
                    onConfirmar={async (estadoDestino, extras) => {
                        await patchServicio(cobrar.id, estadoDestino, estadoDestino === 'COBRADO' ? 'Cobrado' : 'Listo para facturar', extras || {});
                        setCobrar(null);
                    }}
                    onCerrar={() => setCobrar(null)} />
            )}
            <NuevoSheet open={nuevoAbierto} onClose={() => setNuevoAbierto(false)} onElegir={elegirNuevo} conEmpresas={empresasMensuales.length > 0} />
            <ElegirEmpresaSheet empresas={elegirEmpresa} onClose={() => setElegirEmpresa(null)} onElegir={setPedidoDe} />
            {pedidoDe && <NuevoPedidoSheet comoEmpresa={pedidoDe} onCerrar={() => setPedidoDe(null)}
                onCreado={() => { setPedidoDe(null); cargar(); }} />}

            <TrabajoMenu fila={menuFila} onClose={() => setMenuFila(null)} on={accionesMenu} />
            {editor && (
                <TrabajoEditorModal modo={editor.modo} servicio={editor.servicio} clienteInicialId={editor.clienteId}
                    onCerrar={() => setEditor(null)} onGuardado={cargar}
                    orden={editor.orden} onReprogramar={editor.orden ? () => { setReprogramar(editor.orden); setEditor(null); } : null} />
            )}
            {nuevaVisita && <VisitaForm tecnicos={tecnicos} onGuardado={() => { setNuevaVisita(false); cargar(); }} onCancelar={() => setNuevaVisita(false)}
                onPedidoEmpresa={(e) => { setNuevaVisita(false); setPedidoDe(e); }} />}
            {confirmarMasivo && (
                <ConfirmDialog
                    titulo={`Eliminar ${seleccion.size} trabajo${seleccion.size !== 1 ? 's' : ''}`}
                    mensaje="Se borran definitivamente con sus ítems y repuestos. No se puede deshacer."
                    textoConfirmar="Sí, eliminar"
                    onCancelar={() => setConfirmarMasivo(false)}
                    onConfirmar={() => { setConfirmarMasivo(false); masivo('eliminar'); }} />
            )}
            {confirmar && (
                <ConfirmDialog
                    titulo={{ eliminar: 'Eliminar trabajo', archivar: 'Archivar trabajo', cancelarVisita: 'Cancelar visita', eliminarVisita: 'Eliminar visita' }[confirmar.tipo]}
                    mensaje={{
                        eliminar: 'No se puede deshacer: se borra el trabajo con sus ítems y repuestos.',
                        archivar: 'Sale de la lista. Lo podés recuperar desde Archivados.',
                        cancelarVisita: `${confirmar.orden?.clienteNombre || 'La visita'} sale de la agenda del técnico. Queda en el historial como cancelada.`,
                        eliminarVisita: `Se borra la visita de ${confirmar.orden?.clienteNombre || 'este cliente'} del todo. No se puede deshacer.`,
                    }[confirmar.tipo]}
                    textoConfirmar={{ eliminar: 'Sí, eliminar', archivar: 'Sí, archivar', cancelarVisita: 'Sí, cancelar', eliminarVisita: 'Sí, eliminar' }[confirmar.tipo]}
                    onCancelar={() => setConfirmar(null)}
                    onConfirmar={() => {
                        const { tipo, servicio, orden } = confirmar;
                        setConfirmar(null);
                        if (tipo === 'cancelarVisita' || tipo === 'eliminarVisita') accionVisita(tipo, orden);
                        else if (tipo === 'eliminar') eliminar(servicio);
                        else patchServicio(servicio.id, 'ARCHIVADO', 'Archivado');
                    }} />
            )}
            {pedidoAbierto && <PedidoDetalle key={pedidoAbierto.id} pedido={pedidoAbierto} modo="admin"
                onCerrar={() => { setPedidoAbierto(null); cargar(); }} onCambio={cargar} onAgendar={(p) => setAgendarPedido(p)} />}
            {armarDia && <ArmarDiaSheet tecnicos={tecnicos} soloPedidos={armarDia.solo || null} fechaInicial={armarDia.fecha || null} onCerrar={() => setArmarDia(false)}
                onListo={() => { setArmarDia(false); salirSeleccion(); cargar(); }} />}
            {rutaEmpresa && <ConfirmarRutaSheet ruta={rutaEmpresa} tecnicos={tecnicos} onCerrar={() => setRutaEmpresa(null)}
                onListo={() => { setRutaEmpresa(null); cargar(); }} />}
            {agendarPedido && <AgendarPedidoSheet pedido={agendarPedido} tecnicos={tecnicos} onCerrar={() => setAgendarPedido(null)}
                onAgendado={() => { setAgendarPedido(null); setPedidoAbierto(null); cargar(); }} />}
            {detalle && (() => {
                const f = filas.find(x => x.servicio?.id === detalle.id) || filasArchivadas.find(x => x.servicio?.id === detalle.id) || filasExtra.find(x => x.servicio?.id === detalle.id);
                const orden = f?.orden || null;
                const total = f?.monto ?? (Number(detalle.montoFinal) > 0 ? Number(detalle.montoFinal) : totalTrabajo(detalle));
                const editable = !['COBRADO', 'ARCHIVADO', 'CANCELADO'].includes(detalle.estado);
                return (
                    <DetalleTrabajoSheet key={detalle.id} servicio={detalle} orden={orden} total={total}
                        textoCerrar={cierreCliente?.mes ? `← Volver a ${nombreMes(cierreCliente.mes).split(' ')[0].toLowerCase()}` : null}
                        onCerrar={() => setDetalle(null)}
                        onEditar={editable ? () => { setDetalle(null); setEditor({ modo: 'editar', servicio: detalle, orden }); } : null}
                        onReprogramar={orden ? () => { setDetalle(null); setReprogramar(orden); } : null}
                        onCerrarTrabajo={!trabajoHecho(detalle) && !['ARCHIVADO', 'CANCELADO'].includes(detalle.estado)
                            ? () => { const s = detalle; setDetalle(null); setEjecutar(s); } : null}
                        onPDF={() => pdf(detalle)}
                        onCorregirCobro={(trabajoHecho(detalle) || detalle.estado === 'ARCHIVADO') ? () => setCorregir({ servicio: detalle, total }) : null}
                        onPDFResumen={trabajoHecho(detalle) ? () => pdf(detalle, { soloResumen: true }) : null}
                        onFotos={(fotosTrabajo) => { setDetalle(d => (d ? { ...d, fotosTrabajo } : d)); cargar(); }}
                        onProximo={(proximoMantenimiento) => { setDetalle(d => (d ? { ...d, proximoMantenimiento } : d)); cargar(); }}
                        onFotosEquipo={(id, f) => { setDetalle(d => (d ? { ...d, items: (d.items || []).map(it => (it.id === id ? { ...it, ...f } : it)) } : d)); cargar(); }} />
                );
            })()}
            {corregir && <CorregirCobroSheet servicio={corregir.servicio} total={corregir.total} onCerrar={() => setCorregir(null)}
                onGuardado={(s) => { setCorregir(null); setDetalle(s); cargar(); }} />}
            {/* Mientras se mira un trabajo del mes, el cierre queda detrás y vuelve al cerrarlo */}
            {cierreCliente && !detalle && !editor && !reprogramar && !cobrar && (
                <CierreMensualModal cliente={cierreCliente} mesInicial={cierreCliente.mes} periodoInicial={cierreCliente.periodo}
                    onClose={() => { setCierreCliente(null); cargar(); }}
                    onAbrirServicio={(id) => { const s = servicios.find(x => x.id === id); if (s) setDetalle(s); else toast('Ese trabajo no está en la lista (puede estar archivado)'); }}
                    onCambio={cargar}
                    onMes={(mes) => setCierreCliente(c => (c && c.mes !== mes ? { ...c, mes } : c))}
                    onEditarServicio={(id) => { const s = servicios.find(x => x.id === id); if (s) setEditor({ modo: 'editar', servicio: s, orden: null }); }} />
            )}

            {reprogramar && (
                <ReprogramarSheet orden={reprogramar} tecnicos={tecnicos}
                    onCerrar={() => setReprogramar(null)}
                    onGuardado={() => { setReprogramar(null); cargar(); }}
                    onEditarTodo={() => { setOrdenEditar(reprogramar); setReprogramar(null); }} />
            )}
            {ordenEditar && (
                <ModalShell titulo="Editar visita" subtitulo={ordenEditar.clienteNombre || ordenEditar.titulo} onCerrar={() => setOrdenEditar(null)}
                    pie={(
                        <div className="grid grid-cols-2 gap-3">
                            <button type="button" onClick={() => setOrdenEditar(null)} className="h-12 rounded-xl font-bold text-body bg-chip text-secondary active:scale-95">Cancelar</button>
                            <button type="submit" form="form-editar-visita" className="h-12 rounded-xl font-black text-body bg-brand-red text-white active:scale-95">Guardar cambios</button>
                        </div>
                    )}>
                    <OrdenForm orden={ordenEditar} tecnicos={tecnicos} formId="form-editar-visita"
                        onReprogramar={() => { setReprogramar(ordenEditar); setOrdenEditar(null); }}
                        onGuardar={(form) => guardarOrden(ordenEditar.id, form)}
                        onCancelar={() => setOrdenEditar(null)} />
                </ModalShell>
            )}

            {ficha && (
                <FichaVisitaSheet key={ficha.id} orden={ficha} onCerrar={() => setFicha(null)}
                    onEliminada={() => { setFicha(null); cargar(); }} onCambio={cargar} />
            )}
            {atrasada && (
                <div className="fixed inset-0 z-[3000] flex items-end md:items-center md:justify-center bg-black/60 md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6" onClick={() => setAtrasada(null)}>
                    <div className="w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 bg-card space-y-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <p className="text-label font-black text-muted uppercase tracking-widest">Visita atrasada</p>
                                <p className="text-body-lg font-black text-ink">{atrasada.cliente}</p>
                                <p className="text-caption text-muted flex items-center gap-1"><LuMapPin size={12} />{atrasada.fecha} · {primerNombre(atrasada.tecnico) || 'sin técnico'} · {atrasada.nota}</p>
                            </div>
                            <button onClick={() => setAtrasada(null)} aria-label="Cerrar" className="w-9 h-9 rounded-xl flex items-center justify-center bg-chip text-muted"><LuX size={16} /></button>
                        </div>
                        <button onClick={() => { setReprogramar(atrasada.orden); setAtrasada(null); }}
                            className="w-full h-12 rounded-xl font-black text-label uppercase bg-[#C9341F] text-white active:scale-95">Reprogramar para otro día</button>
                        <button onClick={() => patchOrden(atrasada.orden.id, 'COMPLETADA', 'Visita marcada como hecha')}
                            className="w-full h-12 rounded-xl font-bold text-label bg-chip text-ink active:scale-95">Ya se hizo (marcar completada)</button>
                        <button onClick={() => patchOrden(atrasada.orden.id, 'CANCELADA', 'Visita cancelada')}
                            className="w-full h-12 rounded-xl font-bold text-label text-muted border border-black/10 dark:border-white/10 active:scale-95">Cancelar visita</button>
                    </div>
                </div>
            )}
        </div>
    );
}
